"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import type { SerializedEditorState } from "lexical";
import dynamic from "next/dynamic";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { createThread } from "@/query/forum";

// The editor touches `document` during render — never SSR it
const Editor = dynamic(() => import("@/components/editor/editor").then((m) => m.Editor), { ssr: false });

type Props = {
  categoryId: number;
};

export function CreateThreadForm({ categoryId }: Props) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState<SerializedEditorState | null>(null);
  const [isPending, startTransition] = useTransition();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content) {
      toast.error("Bitte Titel und Inhalt ausfüllen.");
      return;
    }
    startTransition(async () => {
      try {
        const threadId = await createThread(categoryId, title.trim(), JSON.stringify(content));
        toast.success("Thema erstellt!");
        setOpen(false);
        setTitle("");
        setContent(null);
        router.push(`/home/forum/${categoryId}/${threadId}`);
      } catch {
        toast.error("Fehler beim Erstellen des Themas.");
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Neues Thema
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-7xl">
        <DialogHeader>
          <DialogTitle>Neues Thema erstellen</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="thread-title">Titel</Label>
            <Input id="thread-title" placeholder="Thema Titel..." value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label>Inhalt</Label>
            <Editor onSerializedChange={setContent} placeholder="Schreibe deinen Beitrag..." />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Erstellen..." : "Thema erstellen"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
